import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../utils/api'
import { SettingsIcon, DollarIcon } from '../components/Icons'

interface StripeConfig {
  publishable_key: string
  enabled: boolean
  webhook_endpoint: string
  api_key_configured: boolean
  webhook_secret_configured: boolean
}

const AdminStripeSettings: React.FC = () => {
  const { isAdmin } = useAuth()
  const [config, setConfig] = useState<StripeConfig>({
    publishable_key: '',
    enabled: false,
    webhook_endpoint: '',
    api_key_configured: false,
    webhook_secret_configured: false
  })
  const [formData, setFormData] = useState({
    api_key: '',
    publishable_key: '',
    webhook_secret: '',
    enabled: false
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<any>(null)

  useEffect(() => {
    if (isAdmin) {
      fetchStripeConfig()
    }
  }, [isAdmin])

  const fetchStripeConfig = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/stripe/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setConfig(data.config)
        setFormData(prev => ({
          ...prev,
          publishable_key: data.config.publishable_key,
          enabled: data.config.enabled
        }))
      } else {
        setError('Failed to fetch Stripe configuration')
      }
    } catch (error) {
      console.error('Error fetching Stripe configuration:', error)
      setError('Failed to fetch Stripe configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setError(null)
    setSuccess(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isAdmin) return

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/stripe/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess('Stripe configuration updated successfully')
        await fetchStripeConfig() // Refresh the config
        setFormData(prev => ({
          ...prev,
          api_key: '', // Clear sensitive fields after save
          webhook_secret: ''
        }))
      } else {
        setError(data.message || 'Failed to update Stripe configuration')
      }
    } catch (error) {
      console.error('Error updating Stripe configuration:', error)
      setError('Failed to update Stripe configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (!isAdmin) return

    try {
      setTesting(true)
      setTestResult(null)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/stripe/test`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()

      if (response.ok) {
        setTestResult(data.test_results)
        setSuccess('Stripe connection test successful')
      } else {
        setError(data.message || 'Stripe connection test failed')
      }
    } catch (error) {
      console.error('Error testing Stripe connection:', error)
      setError('Failed to test Stripe connection')
    } finally {
      setTesting(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            You need administrator privileges to access this page.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
            <DollarIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Stripe Configuration</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Configure Stripe payment processing and API settings
            </p>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`mb-6 p-4 rounded-lg border ${
        config.enabled 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
      }`}>
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            config.enabled ? 'bg-green-500' : 'bg-yellow-500'
          }`}></div>
          <div>
            <p className={`font-medium ${
              config.enabled 
                ? 'text-green-800 dark:text-green-300' 
                : 'text-yellow-800 dark:text-yellow-300'
            }`}>
              Stripe is {config.enabled ? 'Enabled' : 'Disabled'}
            </p>
            <p className={`text-sm ${
              config.enabled 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-yellow-600 dark:text-yellow-400'
            }`}>
              {config.enabled 
                ? 'Payment processing is active and accepting transactions'
                : 'Payment processing is disabled - members cannot make payments'
              }
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Configuration Form */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">API Configuration</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="enabled"
                    checked={formData.enabled}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-gray-900 shadow-sm focus:border-gray-900 focus:ring-gray-900"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                    Enable Stripe Payment Processing
                  </span>
                </label>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Allow members to make payments and manage subscriptions
                </p>
              </div>

              {/* API Key */}
              <div>
                <label htmlFor="api_key" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Secret API Key
                </label>
                <input
                  type="password"
                  id="api_key"
                  name="api_key"
                  value={formData.api_key}
                  onChange={handleInputChange}
                  placeholder={config.api_key_configured ? '••••••••••••••••' : 'sk_test_...'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {config.api_key_configured ? 'API key is configured (leave blank to keep current)' : 'Enter your Stripe secret API key'}
                </p>
              </div>

              {/* Publishable Key */}
              <div>
                <label htmlFor="publishable_key" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Publishable Key
                </label>
                <input
                  type="text"
                  id="publishable_key"
                  name="publishable_key"
                  value={formData.publishable_key}
                  onChange={handleInputChange}
                  placeholder="pk_test_..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  This key is used on the frontend for client-side operations
                </p>
              </div>

              {/* Webhook Secret */}
              <div>
                <label htmlFor="webhook_secret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Webhook Secret
                </label>
                <input
                  type="password"
                  id="webhook_secret"
                  name="webhook_secret"
                  value={formData.webhook_secret}
                  onChange={handleInputChange}
                  placeholder={config.webhook_secret_configured ? '••••••••••••••••' : 'whsec_...'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {config.webhook_secret_configured ? 'Webhook secret is configured (leave blank to keep current)' : 'Optional: Webhook endpoint secret for secure event handling'}
                </p>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                  <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                  <p className="text-green-800 dark:text-green-300 text-sm">{success}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-blue-400 text-white font-medium rounded-md transition-colors flex items-center space-x-2"
                >
                  {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing || !config.api_key_configured}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-md transition-colors flex items-center space-x-2"
                >
                  {testing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  <span>{testing ? 'Testing...' : 'Test Connection'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Status & Test Results */}
        <div className="space-y-6">
          {/* Configuration Status */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configuration Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">API Key</span>
                <span className={`text-sm font-medium ${
                  config.api_key_configured 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {config.api_key_configured ? 'Configured' : 'Not Set'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Publishable Key</span>
                <span className={`text-sm font-medium ${
                  config.publishable_key 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {config.publishable_key ? 'Configured' : 'Not Set'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Webhook Secret</span>
                <span className={`text-sm font-medium ${
                  config.webhook_secret_configured 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {config.webhook_secret_configured ? 'Configured' : 'Optional'}
                </span>
              </div>
            </div>
          </div>

          {/* Test Results */}
          {testResult && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Connection Test Results</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">API Connection</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    {testResult.api_connected ? 'Success' : 'Failed'}
                  </span>
                </div>
                {testResult.account_currency && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Account Currency</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {testResult.account_currency.toUpperCase()}
                    </span>
                  </div>
                )}
                {testResult.available_balance !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Available Balance</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      ${(testResult.available_balance / 100).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Webhook Endpoint */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800 p-6">
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-3">Webhook Endpoint</h3>
            <p className="text-purple-800 dark:text-purple-300 text-sm mb-4">
              Configure this endpoint in your Stripe Dashboard to receive payment events.
            </p>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700 p-3 mb-4">
              <div className="flex items-center justify-between">
                <code className="text-sm text-gray-900 dark:text-gray-100 break-all">
                  {config.webhook_endpoint || `${(import.meta as any).env?.VITE_API_BASE_URL || ''}/api/stripe/webhooks`}
                </code>
                <button
                  onClick={() => {
                    const url = config.webhook_endpoint || `${(import.meta as any).env?.VITE_API_BASE_URL || ''}/api/stripe/webhooks`
                    navigator.clipboard.writeText(url)
                    setSuccess('Webhook URL copied to clipboard!')
                    setTimeout(() => setSuccess(null), 3000)
                  }}
                  className="ml-2 px-2 py-1 text-xs bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
            <p className="text-purple-700 dark:text-gray-600 text-xs">
              ⚠️ Supports both snapshot and thin webhook payloads
            </p>
          </div>

          {/* Database Maintenance */}
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-6">
            <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-300 mb-3">🔧 Database Maintenance</h3>
            <p className="text-orange-800 dark:text-orange-300 text-sm mb-4">
              Clear mock/test Stripe customer data from development and testing.
            </p>
            <button
              onClick={async () => {
                if (!confirm('This will clear all mock Stripe customer IDs (starting with cus_mock_). Users will need to set up billing again. Continue?')) {
                  return
                }
                
                setClearing(true)
                setError(null)
                setSuccess(null)
                
                try {
                  const token = localStorage.getItem('token')
                  const response = await fetch(`${API_BASE_URL}/api/admin/clear-mock-stripe-data`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                  })
                  
                  const data = await response.json()
                  
                  if (response.ok) {
                    setSuccess(`✅ Successfully cleared ${data.cleared_count} mock Stripe customer record(s)`)
                  } else {
                    setError(data.message || 'Failed to clear mock data')
                  }
                } catch (error) {
                  console.error('Error clearing mock data:', error)
                  setError('Failed to clear mock data')
                } finally {
                  setClearing(false)
                }
              }}
              disabled={clearing}
              className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
            >
              {clearing ? '🔄 Clearing...' : '🧹 Clear Mock Stripe Data'}
            </button>
            <p className="text-orange-700 dark:text-orange-400 text-xs mt-3">
              ⚠️ This will remove test customer IDs like "cus_mock_123" from the database. Real Stripe customers are not affected.
            </p>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-3">Need Help?</h3>
            <p className="text-blue-800 dark:text-blue-300 text-sm mb-4">
              Get your Stripe API keys from your Stripe Dashboard under Developers → API keys.
            </p>
            <div className="space-y-2">
              <a 
                href="https://dashboard.stripe.com/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
              >
                → Stripe API Keys Dashboard
              </a>
              <a 
                href="https://dashboard.stripe.com/webhooks" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
              >
                → Webhook Configuration Guide
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminStripeSettings 